import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import Tabs, { type TabItem } from '@/components/base/Tabs';
import {
  fetchDriversRatings,
  fetchLaundryReviews,
  type DriverRatingRow,
  type LaundryReviewsResult,
} from '@/services/reviewsService';
import DriversRatingsPanel from './components/DriversRatingsPanel';
import LaundryReviewsPanel from './components/LaundryReviewsPanel';

type ReviewsTab = 'laundry' | 'drivers';

const TAB_IDS: ReviewsTab[] = ['laundry', 'drivers'];

export default function ReviewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const tab: ReviewsTab = tabParam === 'drivers' ? 'drivers' : 'laundry';

  const setTab = (id: ReviewsTab) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'laundry') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  // Laundry reviews (summary + list). Loaded independently so a failure in
  // one section never hides the sections that loaded successfully.
  const [laundry, setLaundry] = useState<LaundryReviewsResult | null>(null);
  const [laundryLoading, setLaundryLoading] = useState(true);
  const [laundryError, setLaundryError] = useState(false);

  const loadLaundry = useCallback(async () => {
    setLaundryLoading(true);
    setLaundryError(false);
    try {
      setLaundry(await fetchLaundryReviews());
    } catch {
      setLaundryError(true);
    } finally {
      setLaundryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLaundry();
  }, [loadLaundry]);

  // Sub-driver ratings.
  const [drivers, setDrivers] = useState<DriverRatingRow[] | null>(null);
  const [driversLoading, setDriversLoading] = useState(true);
  const [driversError, setDriversError] = useState(false);

  const loadDrivers = useCallback(async () => {
    setDriversLoading(true);
    setDriversError(false);
    try {
      setDrivers(await fetchDriversRatings());
    } catch {
      setDriversError(true);
    } finally {
      setDriversLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const tabs: TabItem[] = TAB_IDS.map((id) => ({ id, label: t(`reviews.tab.${id}`) }));

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('reviews.title')}</h1>
        <p className="mt-1 text-sm text-foreground-500">{t('reviews.subtitle')}</p>
      </header>

      <Card padded={false} className="overflow-hidden">
        <div className="border-b border-background-200 px-4 py-3.5 md:px-5">
          <Tabs items={tabs} active={tab} onChange={(id) => setTab(id as ReviewsTab)} />
        </div>

        <div className="p-4 md:p-5">
          {tab === 'laundry' ? (
            <LaundryReviewsPanel
              data={laundry}
              loading={laundryLoading}
              error={laundryError}
              onRetry={loadLaundry}
            />
          ) : (
            <DriversRatingsPanel
              rows={drivers}
              loading={driversLoading}
              error={driversError}
              onRetry={loadDrivers}
              onView={(id) => navigate(`/dashboard/reviews/drivers/${id}`)}
            />
          )}
        </div>
      </Card>

      <p className="flex items-start gap-2 px-1 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
        {t('reviews.note')}
      </p>
    </div>
  );
}